import dayjs from 'dayjs';
import { Query, Role, Visitor, VisitorGroup } from 'graphql/generated/graphqlTypes';
import { generateMockMobileDevice } from 'mocks/graphqlMocks/mobileDevice';
import { removeFields } from 'mocks/graphqlMocks/removeFields';
import { generateMockVisitor } from 'mocks/graphqlMocks/visitor';
import { generateMockVisitorGroup } from 'mocks/graphqlMocks/visitorGroup';
import { generateMockVisitorGroupMembership } from 'mocks/graphqlMocks/visitorGroupMembership';
import { VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';

export function getVisitorGroupsForTenantWithMembersAndRoleDataForQuery(
  getVisitorGroupsForTenant: Array<VisitorGroup>
): Pick<Query, 'getVisitorGroupsForTenant'> {
  return {
    getVisitorGroupsForTenant
  };
}

// GET_VISITOR_GROUPS_FOR_TENANT_WITH_MEMBERS_AND_ROLE only selects id/name/members/type/externalRef/maxNumberOfMembers
const fieldsToRemoveFromVisitorGroups: (keyof VisitorGroup)[] = [
  'accessGrants',
  'allowedAccessGrants',
  'defaultAccessGrants',
  'isAdminGroup',
  'isQrCodeVisitInviterGroup',
  'memberIds',
  'scheduledAccessGrants',
  'tenantId'
];
// members only selects id/name/email/mobileDevices/isEditable/expirationDate/visitorGroupMembership/isAdmin
const fieldsToRemoveFromVisitor: (keyof Visitor)[] = [
  'externalRef',
  'identityProvider',
  'isQrCodeVisitInviter',
  'tenantId'
];
// visitorGroupMembership.visitorGroup only selects id/type
const fieldsToRemoveFromMembershipVisitorGroup: (keyof VisitorGroup)[] = [
  'accessGrants',
  'allowedAccessGrants',
  'defaultAccessGrants',
  'externalRef',
  'isAdminGroup',
  'isQrCodeVisitInviterGroup',
  'maxNumberOfMembers',
  'memberIds',
  'members',
  'name',
  'scheduledAccessGrants',
  'tenantId'
];

export const mockedGetVisitorGroupsForTenantWithMembersAndRoleDataForQuery =
  getVisitorGroupsForTenantWithMembersAndRoleDataForQuery([
    removeFields(
      generateMockVisitorGroup({
        id: 'mockedVisitorGroupIdExternalUsers1',
        members: [
          removeFields(
            generateMockVisitor({
              id: 'mockedVisitorIdExternalUser1',
              name: 'mockedVisitorNameExternalUser1',
              email: 'mockedVisitorEmailExternalUser1',
              mobileDevices: [generateMockMobileDevice({ id: 'mockedMobileDevice', isRegistered: false })],
              isEditable: true,
              visitorGroupMembership: [
                generateMockVisitorGroupMembership({
                  visitorGroup: removeFields(
                    generateMockVisitorGroup({
                      id: 'mockedVisitorGroupIdExternalUsers1',
                      type: VisitorGroupType.CoContractors
                    }),
                    fieldsToRemoveFromMembershipVisitorGroup
                  ),
                  role: Role.ExternalGroupUser
                })
              ]
            }),
            fieldsToRemoveFromVisitor
          ),
          removeFields(
            generateMockVisitor({
              id: 'mockedVisitorIdExternalUser2',
              name: 'mockedVisitorNameExternalUser2',
              email: 'mockedVisitorEmailExternalUser2',
              mobileDevices: [generateMockMobileDevice({ id: 'mockedMobileDevice', isRegistered: false })],
              isEditable: true,
              visitorGroupMembership: [
                generateMockVisitorGroupMembership({
                  visitorGroup: removeFields(
                    generateMockVisitorGroup({
                      id: 'mockedVisitorGroupIdExternalUsers1',
                      type: VisitorGroupType.CoContractors
                    }),
                    fieldsToRemoveFromMembershipVisitorGroup
                  )
                })
              ]
            }),
            fieldsToRemoveFromVisitor
          )
        ],
        name: 'co-contractors1',
        type: VisitorGroupType.CoContractors,
        externalRef: 'mockedVisitorGroupExternalRefs'
      }),
      fieldsToRemoveFromVisitorGroups
    )
  ]);

export const mockedGetVisitorGroupsForTenantWithMembersAndRoleDataAdmin =
  getVisitorGroupsForTenantWithMembersAndRoleDataForQuery([
    generateMockVisitorGroup({
      id: 'mockedIdCleaners',
      members: [
        generateMockVisitor({
          id: 'mockedVisitorIdCleaner1',
          name: 'cleaner1',
          email: 'cleaner1@atlas.com',
          mobileDevices: [
            generateMockMobileDevice({ id: 'mockedMobileDeviceCleaner1', isRegistered: false, role: 'ACCESS' })
          ],
          isAdmin: false,
          isEditable: true,
          visitorGroupMembership: [
            generateMockVisitorGroupMembership({
              visitorGroup: generateMockVisitorGroup({
                id: 'mockedIdCleaners'
              }),
              role: Role.ExternalGroupUserManager
            })
          ]
        }),
        generateMockVisitor({
          id: 'mockedVisitorIdCleaner2',
          name: 'cleaner2',
          email: 'cleaner2@atlas.com',
          mobileDevices: [
            generateMockMobileDevice({ id: 'mockedMobileDeviceCleaner2', isRegistered: true, role: 'ACCESS' })
          ],
          isAdmin: false,
          isEditable: true,
          visitorGroupMembership: [
            generateMockVisitorGroupMembership({
              visitorGroup: generateMockVisitorGroup({
                id: 'mockedIdCleaners'
              }),
              role: Role.ExternalGroupUser
            })
          ]
        })
      ],
      name: 'cleaners',
      type: VisitorGroupType.CoContractors,
      externalRef: 'mockedExternalRefCleaners'
    }),
    generateMockVisitorGroup({
      id: 'mockedIdCo-contractors',
      members: [
        generateMockVisitor({
          id: 'mockedVisitorIdCo-contractor1',
          name: 'co-contractor1',
          email: 'co-contractor1@atlas.com',
          mobileDevices: [
            generateMockMobileDevice({ id: 'mockedMobileDeviceCo-Contractor1', isRegistered: false, role: 'ACCESS' })
          ],
          isEditable: true,
          isAdmin: false,
          visitorGroupMembership: [
            generateMockVisitorGroupMembership({
              visitorGroup: generateMockVisitorGroup({
                id: 'mockedIdCo-contractors'
              }),
              role: Role.ExternalGroupUserManager
            })
          ],
          expirationDate: dayjs(dayjs().add(1, 'hour')).toISOString()
        }),
        generateMockVisitor({
          id: 'mockedVisitorIdCo-contractor2',
          name: 'co-contractor2',
          email: 'co-contractor2@atlas.com',
          mobileDevices: [
            generateMockMobileDevice({ id: 'mockedMobileDeviceCo-Contractor2', isRegistered: false, role: 'ACCESS' })
          ],
          isEditable: true,
          isAdmin: false,
          visitorGroupMembership: [
            generateMockVisitorGroupMembership({
              visitorGroup: generateMockVisitorGroup({
                id: 'mockedVisitorIdCo-contractor2'
              }),
              role: Role.ExternalGroupUserManager
            })
          ],
          expirationDate: dayjs(dayjs().add(1, 'hour').add(1, 'day')).toISOString()
        }),
        generateMockVisitor({
          id: 'mockedVisitorIdCo-contractor3',
          name: 'co-contractor3',
          email: 'co-contractor3@atlas.com',
          mobileDevices: [
            generateMockMobileDevice({ id: 'mockedMobileDeviceCo-Contractor3', isRegistered: false, role: 'ACCESS' })
          ],
          isEditable: true,
          isAdmin: false,
          visitorGroupMembership: [
            generateMockVisitorGroupMembership({
              visitorGroup: generateMockVisitorGroup({
                id: 'mockedVisitorIdCo-contractor3'
              }),
              role: Role.ExternalGroupUserManager
            })
          ],
          expirationDate: dayjs(dayjs().add(1, 'hour').add(1, 'week')).toISOString()
        })
      ],
      name: 'co-contractors',
      type: VisitorGroupType.CoContractors,
      externalRef: 'mockedExternalRefCo-contractors'
    })
  ]);

export const mockedGetVisitorGroupsForTenant = getVisitorGroupsForTenantWithMembersAndRoleDataForQuery([]);
